/**
 * OAuth 시작 URL 헬퍼.
 *
 * 레거시 (`/auth/youtube`, `/auth/twitch`, `/signout`, `/logout`)와
 * 신규 ggc-be (`/influencer/auth/{youtube,twitch,logout}`) 사이를
 * `useNewInfluencerAuth()` 플래그로 스위칭한다.
 *
 * 신규 BE 매핑 (ggc-be/src/api/modules/influencer-auth/routes.ts):
 *   GET /influencer/auth/youtube[?grantAnalytic=yes]
 *   GET /influencer/auth/twitch
 *   GET /influencer/auth/logout
 *   GET /influencer/auth/error/login  (BE 주도, FE 호출 없음)
 */

import { useNewInfluencerAuth } from '@/lib/config/apiFlags';

/**
 * 직접 호출 모드용 신규 BE 베이스 URL.
 *
 * 우선순위:
 *   1. VITE_NEW_API_BASE_URL (명시적 override)
 *   2. VITE_NEW_API_PROXY_TARGET (vite 프록시 타겟과 공유)
 *   3. prod.localhost: 로컬 프로덕션 빌드 테스트 → 스테이징 BE
 *   4. 프로덕션 빌드: https://api.ggcontent.com (미배포, 플래그 ON 시 자동 사용)
 *   5. 그 외(dev/staging): https://api-staging.ggcontent.com
 */
function isStagingBackend(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'influencer2-staging.ggcontent.com';
}

function resolveNewAuthServer(): string {
  if (import.meta.env.VITE_NEW_API_BASE_URL) return import.meta.env.VITE_NEW_API_BASE_URL;
  if (import.meta.env.VITE_NEW_API_PROXY_TARGET) return import.meta.env.VITE_NEW_API_PROXY_TARGET;
  if (import.meta.env.PROD) {
    if (isStagingBackend()) return 'https://api-staging.ggcontent.com';
    return 'https://api.ggcontent.com';
  }
  return 'https://api-staging.ggcontent.com';
}

/** 직접 호출 모드용 레거시 BE 베이스 URL (기존 Login.tsx와 동일 우선순위) */
const LEGACY_AUTH_SERVER =
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_PROXY_TARGET ||
  'https://gmchanger-dev.herokuapp.com';

interface UrlOptions {
  /**
   * true면 절대 URL 반환 (백엔드 호스트로 직접 이동).
   * false/생략이면 same-origin 상대 경로 — vite/배포 프록시 경유.
   */
  direct?: boolean;
}

interface YoutubeOptions extends UrlOptions {
  /** 신규/레거시 모두 지원하는 추가 권한 요청 플래그. */
  grantAnalytic?: boolean;
}

function withBase(direct: boolean | undefined, useNew: boolean): string {
  if (!direct) return '';
  return useNew ? resolveNewAuthServer() : LEGACY_AUTH_SERVER;
}

export function getYoutubeAuthUrl(options: YoutubeOptions = {}): string {
  const useNew = useNewInfluencerAuth();
  const base = withBase(options.direct, useNew);
  const path = useNew ? '/influencer/auth/youtube' : '/auth/youtube';
  const query = options.grantAnalytic ? '?grantAnalytic=yes' : '';
  return `${base}${path}${query}`;
}

export function getTwitchAuthUrl(options: UrlOptions = {}): string {
  const useNew = useNewInfluencerAuth();
  const base = withBase(options.direct, useNew);
  // 레거시는 trailing slash `/auth/twitch/`로 호출하던 관례를 그대로 유지.
  const path = useNew ? '/influencer/auth/twitch' : '/auth/twitch/';
  return `${base}${path}`;
}

export function getLogoutUrl(options: UrlOptions = {}): string {
  const useNew = useNewInfluencerAuth();
  const base = withBase(options.direct, useNew);
  // 레거시 zoku는 `/signout` 사용 (UserMenu) — 신규는 `/influencer/auth/logout` 단일.
  const path = useNew ? '/influencer/auth/logout' : '/signout';
  return `${base}${path}`;
}
